<template>
  <div :class="`${prefixClass}-calendar ${prefixClass}-calendar-panel-date`">
    <div :class="`${prefixClass}-calendar-header`">
      <sButton
        v-if="!hideDoubleArrows"
        :class="`${prefixClass}-calendar-btn-icon-double-left`"
        :disabled="isDisabledArrows('last-year')"
        icon="double-arrow"
        icon-size="10px"
        small
        type="text"
        @click="handleIconDoubleLeftClick"/>
      <sButton
        :class="`${prefixClass}-calendar-btn-icon-left`"
        :disabled="isDisabledArrows('last-month')"
        icon="arrow"
        icon-class="s-svg-icon-rotate-90"
        icon-size="10px"
        small
        type="text"
        @click="handleIconLeftClick"/>
      <sButton
        v-if="!hideDoubleArrows"
        :class="`${prefixClass}-calendar-btn-icon-double-right`"
        :disabled="isDisabledArrows('next-year')"
        icon="double-arrow"
        icon-class="s-svg-icon-rotate-180"
        icon-size="10px"
        small
        type="text"
        @click="handleIconDoubleRightClick"/>
      <sButton
        :class="`${prefixClass}-calendar-btn-icon-right`"
        :disabled="isDisabledArrows('next-month')"
        icon="arrow"
        icon-class="s-svg-icon-rotate--90"
        icon-size="10px"
        small
        type="text"
        @click="handleIconRightClick"/>
      <span :class="`${prefixClass}-calendar-header-label`">
        <sButton
          v-for="item in yearMonth"
          :key="item.panel"
          :class="
            `${prefixClass}-calendar-btn-current-${item.panel}`
          "
          small
          type="text"
          @click="handlePanelChange(item.panel)">
          {{ item.label }}
        </sButton>
      </span>
    </div>
    <div :class="`${prefixClass}-calendar-content`">
      <table :class="`${prefixClass}-table ${prefixClass}-table-date`">
        <thead>
          <tr>
            <th
              v-for="day in days"
              :key="day">
              {{ day }}
            </th>
          </tr>
        </thead>
        <tbody @click="handleCellClick">
          <tr
            v-for="(row, i) in dates"
            :key="i"
            :class="[`${prefixClass}-date-row`, getRowClasses(row)]">
            <td
              v-for="(cell, j) in row"
              :key="j"
              class="cell"
              :class="getCellClasses(cell)"
              :data-row-col="`${i},${j}`"
              :title="getCellTitle(cell)"
              @mouseenter="handleMouseEnter(cell)"
              @mouseleave="handleMouseLeave(cell)">
              <div>{{ cell.getDate() }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { getWeek } from 'toolkit/utils/date-format-parse/util.js'
import { format, getCalendar, setMonth, setYear, chunk } from 'toolkit/utils/date'
import { getLocale } from 'toolkit/components/dateComponents/locale'

export default {
  name: 'sTableDate',
  inject: {
    /*getLocale: {
      default: () => getLocale,
    },*/
    getWeek: {
      default: () => getWeek,
    },
    prefixClass: {
      default: 's-datepicker',
    },
    onDateMouseEnter: {
      default: undefined,
    },
    onDateMouseLeave: {
      default: undefined,
    },
    hideDoubleArrows: {
      type: Boolean,
      default: false,
    },
  },
  props: {
    disabledCalendarChanger: {
      type: Function,
      default: () => false,
    },
    calendar: {
      type: Date,
      default: () => new Date(),
    },
    titleFormat: {
      type: String,
      default: 'DD.MM.YYYY',
    },
    getRowClasses: {
      type: Function,
      default: () => [],
    },
    getCellClasses: {
      type: Function,
      default: () => [],
    },
  },
  computed: {
    firstDayOfWeek() {
      return getLocale(this.$i18n.locale).formatLocale.firstDayOfWeek || 0
    },
    yearMonth() {
      const { yearFormat, monthBeforeYear, monthFormat = 'MMM' } = getLocale(this.$i18n.locale)
      const yearLabel = {
        panel: 'year',
        label: this.formatDate(this.calendar, yearFormat),
      }
      const monthLabel = {
        panel: 'month',
        label: this.formatDate(this.calendar, monthFormat),
      }
      return monthBeforeYear ? [monthLabel, yearLabel] : [yearLabel, monthLabel]
    },
    days() {
      const locale = getLocale(this.$i18n.locale)
      const days = locale.days || locale.formatLocale.weekdaysMin
      return days.concat(days).slice(this.firstDayOfWeek, this.firstDayOfWeek + 7)
    },
    dates() {
      const year = this.calendar.getFullYear()
      const month = this.calendar.getMonth()
      const arr = getCalendar({
        firstDayOfWeek: this.firstDayOfWeek,
        year,
        month,
      })
      return chunk(arr, 7)
    },
  },
  methods: {
    isDisabledArrows(type) {
      const date = new Date(this.calendar)
      switch (type) {
      case 'last-year':
        date.setFullYear(date.getFullYear() - 1, date.getMonth() + 1, 0)
        date.setHours(23, 59, 59, 999)
        break
      case 'next-year':
        date.setFullYear(date.getFullYear() + 1)
        break
      case 'last-month':
        date.setMonth(date.getMonth(), 0)
        date.setHours(23, 59, 59, 999)
        break
      case 'next-month':
        date.setMonth(date.getMonth() + 1)
        break
      default:
        break
      }
      return this.disabledCalendarChanger(date, type)
    },
    handleIconLeftClick() {
      this.$emit(
        'changecalendar',
        setMonth(this.calendar, v => v - 1),
        'last-month'
      )
    },
    handleIconRightClick() {
      this.$emit(
        'changecalendar',
        setMonth(this.calendar, v => v + 1),
        'next-month'
      )
    },
    handleIconDoubleLeftClick() {
      this.$emit(
        'changecalendar',
        setYear(this.calendar, v => v - 1),
        'last-year'
      )
    },
    handleIconDoubleRightClick() {
      this.$emit(
        'changecalendar',
        setYear(this.calendar, v => v + 1),
        'next-year'
      )
    },
    handlePanelChange(panel) {
      this.$emit('changepanel', panel)
    },
    handleMouseEnter(cell) {
      if (typeof this.onDateMouseEnter === 'function') {
        this.onDateMouseEnter(cell)
      }
    },
    handleMouseLeave(cell) {
      if (typeof this.onDateMouseLeave === 'function') {
        this.onDateMouseLeave(cell)
      }
    },
    handleCellClick(evt) {
      let { target } = evt
      if (target.tagName.toUpperCase() === 'DIV') {
        target = target.parentNode
      }
      const index = target.getAttribute('data-row-col')
      if (index) {
        const [row, col] = index.split(',').map(v => parseInt(v, 10))
        const date = this.dates[row][col]
        this.$emit('select', new Date(date))
      }
    },
    formatDate(date, fmt) {
      if (!(date instanceof Date) || isNaN(date)) { return '' }
      return format(date, fmt, getLocale(this.$i18n.locale))
    },
    getCellTitle(date) {
      const fmt = this.titleFormat
      return this.formatDate(date, fmt)
    },
  },
}
</script>